import { useState } from 'react';
import { useTracking } from 'react-tracking';

import { Button, Text, Grid } from '@simplywallst/ui-core';

import { useCreateNoteReminder } from '../../hooks/useCreateNoteReminder';
import { useDeleteNoteReminder } from '../../hooks/useDeleteNoteReminder';
import { useNote } from '@/components/Note/hooks/useNote';
import { useUpdateNoteReminder } from '../../hooks/useUpdateNoteReminder';

import NoteReminderPriceForm from '../NoteReminderPriceForm';
import CompanySharePrice from '../CompanySharePrice';
import EventBasedReminderMessage from '../EventBasedReminderMessage';
import NoteReminderAlert from '../NoteReminderAlert';
import NoteReminderHeader from '../NoteReminderHeader';

import {
  generateEventBasedNoteReminderPayload,
  validateTargetSharePrice,
} from '@/components/NoteReminder/utils';

import { StyledGridFooter } from './styled';

type NoteReminderProps = {
  companyId: string;
  noteId: string;
  goBack: () => void;
};

export const NoteReminder = ({
  companyId,
  noteId,
  goBack,
}: NoteReminderProps) => {
  const { data: note, status: noteStatus } = useNote(noteId);
  const {
    mutate: createNoteReminder,
    status: createNoteReminderStatus,
    reset: resetCreateNoteReminder,
  } = useCreateNoteReminder({ companyId, noteId });
  const {
    mutate: deleteNoteReminder,
    status: deleteNoteReminderStatus,
    reset: resetDeleteNoteReminder,
  } = useDeleteNoteReminder();
  const {
    mutate: updateNoteReminder,
    status: updateNoteReminderStatus,
    reset: resetUpdateNoteReminder,
  } = useUpdateNoteReminder();

  const statusMap = {
    useCreateNoteReminder: {
      reset: resetCreateNoteReminder,
      status: createNoteReminderStatus,
      action: 'create',
    },
    useNote: {
      reset: () => undefined,
      status: noteStatus,
      action: 'read',
    },
    useUpdateNoteReminder: {
      reset: resetUpdateNoteReminder,
      status: updateNoteReminderStatus,
      action: 'update',
    },
    useDeleteNoteReminder: {
      reset: resetDeleteNoteReminder,
      status: deleteNoteReminderStatus,
      action: 'delete',
    },
  };

  const [formFields, setFormFields] = useState({ targetSharePrice: '' });

  const { Track: NoteReminderTrack, trackEvent: noteReminderTrackEvent } =
    useTracking({
      type: 'track',
      subject: 'reminder',
    });

  const isValidForm = validateTargetSharePrice(
    formFields.targetSharePrice,
    note?.company?.analysisValue?.lastSharePrice ?? undefined
  );
  const currentNoteReminder =
    (typeof note?.reminders !== 'undefined' && note?.reminders?.[0]) ||
    undefined;
  const isExistingNoteReminder = typeof currentNoteReminder !== 'undefined';

  const generatePayload = () => {
    if (
      typeof note?.company !== 'undefined' &&
      note?.company?.analysisValue?.lastSharePrice
    ) {
      const currentSharePrice = note?.company?.analysisValue?.lastSharePrice;
      const currencyISO = note?.company?.listingCurrencyISO;
      const parsedTargetSharePrice = parseFloat(formFields.targetSharePrice);
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const operationType =
        parsedTargetSharePrice > currentSharePrice
          ? 'greater_than'
          : 'less_than';

      const targetMetadata = {
        companyCanonicalUrl: note?.company.canonicalURL,
        companyUniqueSymbol: note?.company.uniqueSymbol,
        currencyISO:
          typeof currencyISO !== 'undefined' ? currencyISO : undefined,
      };

      return generateEventBasedNoteReminderPayload({
        companyId,
        noteId,
        operationType,
        targetMetadata,
        targetSharePrice: parsedTargetSharePrice,
        userTimezone,
      });
    }
  };

  const handleOnCreate = () => {
    if (!isExistingNoteReminder) {
      const payload = generatePayload();

      if (typeof payload !== 'undefined') {
        createNoteReminder(payload);

        noteReminderTrackEvent({
          action: 'created',
          noteId,
          reminderType: 'price',
        });
      }
    }
  };

  const handleOnUpdate = () => {
    if (isExistingNoteReminder) {
      const payload = generatePayload();

      if (
        typeof payload !== 'undefined' &&
        typeof payload.eventBasedReminder !== 'undefined'
      ) {
        const {
          operationType,
          targetValue,
          objectIdentifier,
          targetKey,
          eventType,
        } = payload.eventBasedReminder ?? {};

        updateNoteReminder({
          ...payload,
          eventBasedReminder: {
            eventType,
            objectIdentifier,
            operationType,
            targetKey,
            targetValue,
          },
          companyId,
          noteId,
          reminderId: currentNoteReminder.id,
        });

        noteReminderTrackEvent({
          action: 'updated',
          noteId,
          reminderId: currentNoteReminder.id,
          reminderType: 'price',
        });
      }
    }
  };

  const handleOnDelete = () => {
    if (isExistingNoteReminder) {
      deleteNoteReminder({
        companyId,
        noteId,
        reminderId: currentNoteReminder.id,
      });

      setFormFields({ targetSharePrice: '' });

      noteReminderTrackEvent({
        action: 'deleted',
        noteId,
        reminderId: currentNoteReminder.id,
        reminderType: 'price',
      });
    }
  };

  return (
    <NoteReminderTrack>
      <NoteReminderHeader
        goBack={goBack}
        isNoteReminderExisting={isExistingNoteReminder}
      />
      <div className="grid h-full content-between">
        <Grid as="section" padding="x3" gap="8px">
          <div className="grid grid-rows-[max-content_max-content]">
            {noteStatus === 'pending' ? (
              <Text typography="xSmall" color="mediumGray">
                Loading
              </Text>
            ) : (
              <>
                <CompanySharePrice company={note?.company} />
                <NoteReminderPriceForm
                  onChange={(data) => setFormFields(data)}
                  targetSharePrice={
                    currentNoteReminder?.reminderTarget?.targetValue
                  }
                  currentSharePrice={
                    note?.company?.analysisValue?.lastSharePrice ?? undefined
                  }
                />
                <EventBasedReminderMessage
                  company={note?.company}
                  targetSharePrice={formFields.targetSharePrice}
                />
                <NoteReminderAlert
                  hooks={[
                    'useCreateNoteReminder',
                    'useDeleteNoteReminder',
                    'useNote',
                    'useUpdateNoteReminder',
                  ]}
                  statusMap={statusMap}
                  type="error"
                />
                <NoteReminderAlert
                  hooks={[
                    'useCreateNoteReminder',
                    'useDeleteNoteReminder',
                    'useUpdateNoteReminder',
                  ]}
                  statusMap={statusMap}
                  type="success"
                />
              </>
            )}
          </div>
        </Grid>
        <StyledGridFooter
          as="footer"
          templateColumns={`${
            isExistingNoteReminder
              ? 'repeat(2, max-content)'
              : 'repeat(1, max-content)'
          }`}
          gap="16px"
          alignItems="center"
          justifyContent="flex-end"
        >
          {isExistingNoteReminder && (
            <Button
              isLoading={deleteNoteReminderStatus === 'pending'}
              disabled={
                deleteNoteReminderStatus === 'pending' ||
                updateNoteReminderStatus === 'pending'
              }
              styleType="loudLarge"
              onClick={handleOnDelete}
            >
              Remove
            </Button>
          )}
          {isExistingNoteReminder && (
            <Button
              styleType="loudLarge"
              isLoading={updateNoteReminderStatus === 'pending'}
              disabled={
                !isValidForm ||
                noteStatus === 'pending' ||
                deleteNoteReminderStatus === 'pending' ||
                updateNoteReminderStatus === 'pending'
              }
              onClick={handleOnUpdate}
            >
              Update
            </Button>
          )}
          {!isExistingNoteReminder && (
            <Button
              styleType="loudLarge"
              isLoading={createNoteReminderStatus === 'pending'}
              disabled={!isValidForm || createNoteReminderStatus === 'pending'}
              onClick={handleOnCreate}
            >
              Create
            </Button>
          )}
        </StyledGridFooter>
      </div>
    </NoteReminderTrack>
  );
};
